import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  PhoneCall, CalendarCheck, Building2, Lock, ShieldCheck, Award, Hospital,
  Stethoscope, Video, FlaskConical, ArrowRight, Activity, MessageSquare,
  BadgeCheck, CheckCircle2, Star,
} from "lucide-react";
import SEO from "@/components/SEO";
import Marquee from "@/components/Marquee";
import CtaBand from "@/components/CtaBand";
import DoctorPortrait, { CredentialList } from "@/components/DoctorPortrait";
import { Reveal, Eyebrow, SectionTitle, MaskedLine } from "@/components/Reveal";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CLINIC, WHY_CHOOSE, MANIFESTO, PRIVACY_POINTS, JOURNEY, SERVICES, FAQS,
  SYMPTOMS, clinicJsonLd, physicianJsonLd,
} from "@/data/content";

const WHY_ICONS = [Hospital, Award, Stethoscope, Lock, Video, FlaskConical];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <>
      <SEO
        title="Delhi STD Clinic | Dr. Ankur Lal — Confidential STD & Sexual Health Care in Delhi"
        description="Consult Dr. Ankur Lal — STD & Sexual Health Specialist, Ex-STD Specialist & Former Senior Resident, AIIMS New Delhi, Gold Medalist MD Dermatology. Confidential online & clinic consultations for STD/STI, HIV and sexual-health concerns in Delhi."
        path="/"
        jsonLd={[clinicJsonLd, physicianJsonLd]}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-[#FAF9F6] texture-grain" data-testid="hero-section">
        <div className="absolute top-0 right-0 w-[42rem] h-[42rem] rounded-full bg-emerald-900/6 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[30rem] h-[30rem] rounded-full bg-amber-500/8 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 sm:pt-40 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-white border border-emerald-900/15 rounded-full px-4 py-1.5 shadow-sm"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-800" />
              <span className="text-[11px] tracking-[0.2em] uppercase font-semibold text-emerald-950">
                Delhi STD Clinic — Specialist Sexual Health Practice
              </span>
            </motion.div>

            <h1 className="mt-7 font-serif font-semibold text-slate-950 tracking-tight leading-[1.06] text-[2.6rem] sm:text-6xl lg:text-[4.4rem]">
              <MaskedLine delay={0.15}>Confidential STD &</MaskedLine>
              <MaskedLine delay={0.28}>
                <span className="gold-underline">Sexual Health Care</span>
              </MaskedLine>
              <MaskedLine delay={0.41}>in Delhi</MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl"
            >
              Consult <span className="font-semibold text-slate-900">Dr. Ankur Lal</span> — STD &
              Sexual Health Specialist, {CLINIC.credentialLine}, and {CLINIC.medal}.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-3 text-sm sm:text-base text-slate-500 leading-relaxed max-w-xl"
            >
              Expert, confidential and judgment-free consultation for STD/STI, HIV and
              sexual-health concerns. Online and clinic consultations available.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.82, duration: 0.7 }}
              className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3"
            >
              <Link
                to="/booking?type=online"
                data-testid="hero-book-online-button"
                className="inline-flex items-center justify-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-white font-semibold text-sm px-7 py-4 rounded-full transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                <CalendarCheck className="w-4 h-4" /> Book Online Consultation
              </Link>
              <Link
                to="/booking?type=clinic"
                data-testid="hero-book-clinic-button"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-900 border border-slate-300 font-semibold text-sm px-7 py-4 rounded-full transition-all hover:-translate-y-0.5"
              >
                <Building2 className="w-4 h-4" /> Book Clinic Appointment
              </Link>
              <a
                href={`tel:${CLINIC.phoneRaw}`}
                data-testid="hero-call-button"
                className="inline-flex items-center justify-center gap-2 text-emerald-950 font-semibold text-sm px-5 py-4 rounded-full underline underline-offset-8 decoration-emerald-700/40 hover:decoration-emerald-700 transition-all"
              >
                <PhoneCall className="w-4 h-4" /> Call the Clinic
              </a>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-7 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase font-semibold text-emerald-900/80"
              data-testid="hero-privacy-line"
            >
              <Lock className="w-3.5 h-3.5" /> Private • Confidential • Judgment-Free
            </motion.p>
          </div>

          <div className="lg:col-span-5 max-w-md mx-auto w-full lg:max-w-none">
            <DoctorPortrait parallaxY={portraitY} />
          </div>
        </div>
      </section>

      <Marquee />

      {/* MEET THE DOCTOR */}
      <section className="py-20 sm:py-28" data-testid="meet-doctor-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>Meet Your Specialist</Eyebrow>
            <SectionTitle className="mt-4">Meet Dr. Ankur Lal</SectionTitle>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Dr. Ankur Lal is a Dermatologist and STD & Sexual Health Specialist with
              clinical experience in the diagnosis and management of sexually transmitted
              infections and sexual-health conditions. He is a Gold Medalist in MD
              Dermatology and has previously worked as an STD Specialist and Senior
              Resident at AIIMS New Delhi.
            </p>
            <div className="mt-8">
              <CredentialList />
            </div>
            <Link
              to="/about-dr-ankur-lal"
              data-testid="meet-doctor-cta"
              className="mt-9 inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
            >
              Consult Dr. Ankur Lal <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="relative">
            <div className="rounded-[2rem] overflow-hidden border border-slate-900/10 shadow-2xl aspect-[4/3]">
              <img
                src={CLINIC.photo}
                alt="Dr. Ankur Lal at his clinic"
                className="w-full h-full object-cover object-[center_20%]"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-6 left-6 right-6 sm:left-10 sm:right-auto bg-white/95 backdrop-blur border border-slate-900/10 rounded-2xl px-6 py-4 shadow-xl">
              <p className="font-serif text-lg font-semibold text-slate-950 leading-snug">
                "Every patient deserves to be heard — privately, and without judgment."
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-20 sm:py-28 bg-white border-y border-slate-900/8" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Why Choose Us</Eyebrow>
            <SectionTitle className="mt-4">Why Consult Dr. Ankur Lal?</SectionTitle>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE.map((w, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={w.title} delay={i * 0.07}>
                  <div
                    data-testid={`why-card-${i}`}
                    className="group h-full bg-[#FAF9F6] border border-slate-900/10 rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-20px_rgba(6,78,59,0.25)] hover:border-emerald-900/25"
                  >
                    <span className="w-11 h-11 rounded-2xl bg-emerald-900 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-emerald-100" />
                    </span>
                    <h3 className="mt-5 font-serif text-xl font-semibold text-slate-950">{w.title}</h3>
                    <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONFIDENTIALITY MANIFESTO */}
      <section className="py-20 sm:py-28 bg-slate-950 text-slate-100 relative overflow-hidden" data-testid="confidentiality-section">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[50rem] h-[20rem] bg-emerald-900/20 blur-3xl rounded-full pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-semibold tracking-[0.28em] uppercase text-amber-400/90">
              Our Confidentiality Promise
            </p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-white">
              Your Sexual Health. Your Privacy.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl">
              "We understand that discussing sexual health can feel difficult. Our clinic
              provides a private, respectful and judgment-free environment where you can
              discuss your concerns openly with a qualified doctor."
            </p>
          </Reveal>

          <div className="mt-14 grid sm:grid-cols-2 gap-x-12 gap-y-10">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.08}>
                <div className="flex gap-6 items-start border-t border-white/10 pt-7" data-testid={`manifesto-${m.num}`}>
                  <span className="font-serif text-5xl font-light text-amber-400/70 leading-none">{m.num}</span>
                  <div>
                    <h3 className="font-serif text-xl sm:text-2xl font-semibold text-white">{m.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2} className="mt-14">
            <div className="flex flex-wrap gap-2.5" data-testid="privacy-points">
              {PRIVACY_POINTS.map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center gap-2 bg-emerald-950/80 border border-emerald-800/60 text-emerald-100 text-xs font-medium px-4 py-2 rounded-full"
                >
                  <Lock className="w-3 h-3 text-amber-400" /> {p}
                </span>
              ))}
            </div>
            <Link
              to="/privacy-confidentiality"
              data-testid="confidentiality-learn-more"
              className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-amber-300 hover:text-amber-200 transition-colors"
            >
              Read our Privacy & Confidentiality approach <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 sm:py-28" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>STD / STI Services</Eyebrow>
            <SectionTitle className="mt-4">Specialist Treatment, Delhi</SectionTitle>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Focused, guideline-based care for sexually transmitted infections and
              sexual-health conditions — always confidential.
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Reveal>
              <Link
                to="/hiv-pep-delhi"
                data-testid="service-card-hiv-pep"
                className="group flex flex-col h-full bg-amber-50 border border-amber-300/60 rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-20px_rgba(180,83,9,0.35)]"
              >
                <span className="inline-flex w-fit items-center gap-1.5 bg-amber-600 text-white text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1 rounded-full">
                  Time-Sensitive
                </span>
                <h3 className="mt-4 font-serif text-2xl font-semibold text-slate-950">HIV PEP</h3>
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed flex-1">
                  Urgent assessment following a potential HIV exposure. PEP should be
                  started as soon as possible — generally not after 72 hours.
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-800 group-hover:gap-2.5 transition-all">
                  Urgent consultation <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </Reveal>
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.07}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group flex flex-col h-full bg-white border border-slate-900/10 rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-20px_rgba(6,78,59,0.25)] hover:border-emerald-900/25"
                >
                  <h3 className="font-serif text-2xl font-semibold text-slate-950">{s.name}</h3>
                  <p className="mt-2.5 text-sm text-slate-600 leading-relaxed flex-1">{s.gridDesc}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-900 group-hover:gap-2.5 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PATIENT JOURNEY */}
      <section className="py-20 sm:py-28 bg-white border-y border-slate-900/8" data-testid="journey-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <Eyebrow>How It Works</Eyebrow>
            <SectionTitle className="mt-4">Your Patient Journey</SectionTitle>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-5 gap-6">
            {JOURNEY.map((j, i) => (
              <Reveal key={j.step} delay={i * 0.09} className="relative text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-emerald-950 text-amber-300 font-serif text-xl font-semibold flex items-center justify-center border border-emerald-800">
                  {j.step}
                </div>
                {i < JOURNEY.length - 1 && (
                  <div className="hidden md:block absolute top-7 left-[calc(50%+2rem)] right-[calc(-50%+2rem)] h-px bg-gradient-to-r from-emerald-900/40 to-emerald-900/10" />
                )}
                <p className="mt-4 text-xs font-bold tracking-[0.22em] uppercase text-slate-950">{j.label}</p>
                <p className="mt-2 text-[13px] text-slate-500 leading-relaxed">{j.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SYMPTOMS TEASER */}
      <section className="py-20 sm:py-28" data-testid="symptoms-teaser">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow>Know the Signs</Eyebrow>
            <SectionTitle className="mt-4">Common STD/STI Symptoms</SectionTitle>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Discharge, burning during urination, genital ulcers, sores, warts, itching,
              rashes and more — if something feels wrong, a confidential consultation
              gives you clear answers.
            </p>
            <div className="mt-6 flex items-start gap-3 bg-emerald-50 border border-emerald-900/15 rounded-2xl p-5">
              <Activity className="w-5 h-5 text-emerald-900 shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-950 leading-relaxed">
                Many STIs may cause few or no symptoms. Testing and medical assessment may
                therefore be important even if you feel completely well.
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/std-symptoms"
                data-testid="symptoms-teaser-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-900 underline underline-offset-8 decoration-emerald-700/40 hover:decoration-emerald-700"
              >
                View all symptoms <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="flex flex-wrap gap-2.5">
              {SYMPTOMS.slice(0, 9).map((s) => (
                <span
                  key={s}
                  className="bg-white border border-slate-900/10 text-slate-700 text-[13px] font-medium px-4 py-2.5 rounded-full shadow-sm"
                >
                  {s}
                </span>
              ))}
              <Link
                to="/std-symptoms"
                className="bg-emerald-900 text-white text-[13px] font-semibold px-4 py-2.5 rounded-full inline-flex items-center gap-1.5"
              >
                +{SYMPTOMS.length - 9} more <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ONLINE CONSULT TEASER */}
      <section className="py-20 sm:py-28 bg-emerald-950 text-emerald-50 relative overflow-hidden" data-testid="online-teaser">
        <div className="absolute -top-32 -right-32 w-[36rem] h-[36rem] rounded-full bg-emerald-700/25 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.28em] uppercase text-amber-400/90">From Your Home</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-white">
              Online STD Consultation with Dr. Ankur Lal
            </h2>
            <p className="mt-5 text-emerald-100/80 leading-relaxed max-w-xl">
              Private sexual-health consultation from the comfort of your home — for
              symptoms, HIV concerns, PEP/PrEP guidance, test-result interpretation and
              follow-ups.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/online-consultation"
                data-testid="online-teaser-learn"
                className="inline-flex items-center justify-center gap-2 border border-emerald-300/40 text-emerald-50 hover:bg-emerald-900 font-semibold text-sm px-7 py-3.5 rounded-full transition-all"
              >
                <Video className="w-4 h-4" /> How Online Consultation Works
              </Link>
              <Link
                to="/booking?type=online"
                data-testid="online-teaser-book"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm px-7 py-3.5 rounded-full transition-all shadow-lg hover:-translate-y-0.5"
              >
                <CalendarCheck className="w-4 h-4" /> Book Online Consultation
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: Video, label: "Video Consultation" },
                { icon: PhoneCall, label: "Phone Consultation" },
                { icon: Building2, label: "Clinic Consultation" },
              ].map((o) => (
                <div
                  key={o.label}
                  className="bg-emerald-900/50 border border-emerald-800/60 rounded-3xl p-6 text-center backdrop-blur"
                >
                  <o.icon className="w-6 h-6 mx-auto text-amber-300" />
                  <p className="mt-3 text-[13px] font-semibold text-emerald-50 leading-snug">{o.label}</p>
                </div>
              ))}
              <div className="col-span-3 bg-emerald-900/50 border border-emerald-800/60 rounded-3xl p-6 backdrop-blur flex items-start gap-4">
                <Lock className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-100/85 leading-relaxed">
                  Consult from anywhere in India — discreetly. Some conditions may require
                  physical examination, laboratory testing or an in-person consultation.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* REVIEWS PLACEHOLDER */}
      <section className="py-20 sm:py-28" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Patient Voices</Eyebrow>
            <SectionTitle className="mt-4">What Patients Say</SectionTitle>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Given the sensitive nature of sexual-health care, patient reviews are
              published only with explicit consent. Genuine, verified patient
              testimonials will appear here.
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-3 gap-5">
            {[1, 2, 3].map((n) => (
              <Reveal key={n} delay={n * 0.07}>
                <div
                  data-testid={`review-placeholder-${n}`}
                  className="h-full bg-white border border-dashed border-slate-300 rounded-3xl p-7 flex flex-col"
                >
                  <div className="flex gap-1 text-slate-300">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4" />
                    ))}
                  </div>
                  <p className="mt-4 font-serif text-xl text-slate-400 italic leading-relaxed flex-1">
                    "Verified patient testimonial — to be published with patient consent."
                  </p>
                  <p className="mt-5 text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
                    Verified Patient • Delhi
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="py-20 sm:py-28 bg-white border-y border-slate-900/8" data-testid="faq-preview">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center">
            <Eyebrow>Questions, Answered</Eyebrow>
            <SectionTitle className="mt-4">Frequently Asked Questions</SectionTitle>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <Accordion type="single" collapsible>
              {FAQS.slice(0, 5).map((f, i) => (
                <AccordionItem key={i} value={`home-faq-${i}`} data-testid={`home-faq-${i}`}>
                  <AccordionTrigger className="text-left font-medium text-slate-900">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="mt-8 text-center">
              <Link
                to="/faq"
                data-testid="faq-view-all"
                className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-900 underline underline-offset-8 decoration-emerald-700/40 hover:decoration-emerald-700"
              >
                View all {FAQS.length} questions <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 sm:py-32 bg-slate-950 text-slate-100 relative overflow-hidden texture-grain" data-testid="final-cta">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[46rem] h-[46rem] rounded-full bg-emerald-900/20 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.28em] uppercase text-amber-400/90">
              You Are Not Alone
            </p>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.1] text-white">
              Worried About an STD or Sexual Health Problem?
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl mx-auto">
              You don't have to feel embarrassed or judged. Speak confidentially with
              Dr. Ankur Lal and get professional medical guidance.
            </p>
            <div className="mt-6 flex flex-col items-center gap-1.5 text-sm text-slate-400">
              <p className="inline-flex items-center gap-2">
                <BadgeCheck className="w-4 h-4 text-amber-400" />
                {CLINIC.credentialLine}
              </p>
              <p className="inline-flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-400" />
                {CLINIC.medal}
              </p>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/booking?type=online"
                data-testid="final-book-online"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm px-8 py-4 rounded-full transition-all shadow-lg hover:-translate-y-0.5"
              >
                <CalendarCheck className="w-4 h-4" /> Book Online Consultation
              </Link>
              <Link
                to="/booking?type=clinic"
                data-testid="final-book-clinic"
                className="inline-flex items-center gap-2 border border-slate-600 text-slate-100 hover:bg-slate-900 font-semibold text-sm px-8 py-4 rounded-full transition-all"
              >
                <Building2 className="w-4 h-4" /> Book Clinic Appointment
              </Link>
              <a
                href={`tel:${CLINIC.phoneRaw}`}
                data-testid="final-call"
                className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 font-semibold text-sm px-5 py-4 underline underline-offset-8 decoration-emerald-500/40"
              >
                <PhoneCall className="w-4 h-4" /> Call the Clinic
              </a>
            </div>
            <p className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase font-semibold text-emerald-300/80">
              <Lock className="w-3.5 h-3.5" /> Confidential • Professional • Judgment-Free
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Home;
